
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { BRAND } from '@/brand/brand';
import { configuration } from '../Configuration';
import { Card, CardContent } from '@/components/ui/card';

type BridgeView = 'starting' | 'login' | 'authorizing' | 'redirecting' | 'success' | 'missing' | 'error';

type AppInfo = {
  label: string;
  icon: string;
};

type BridgeStatus = {
  view: BridgeView;
  title: string;
  message: string;
  detail?: string;
  appInfo?: AppInfo;
};

/**
 * Statusfarben laufen über die Tokens (primary / success / warning /
 * destructive) statt über die früheren Hex-Werte der Website-Palette, damit
 * die Brücke dieselbe Statusfarbigkeit trägt wie der Rest der Oberfläche.
 * `dot` färbt den Zustandspunkt, `halo` den weichen Ring darum.
 */
const VIEW_STYLES: Record<
  BridgeView,
  {
    eyebrow: string;
    dot: string;
    halo: string;
    accentText: string;
    spinner: boolean;
  }
> = {
  starting: {
    eyebrow: 'Verbindung',
    dot: 'bg-primary',
    halo: 'bg-primary/10',
    accentText: 'text-primary',
    spinner: true,
  },
  login: {
    eyebrow: 'Anmeldung erforderlich ...',
    dot: 'bg-primary',
    halo: 'bg-primary/10',
    accentText: 'text-primary',
    spinner: true,
  },
  authorizing: {
    eyebrow: 'Prüfe Anmeldung ...',
    dot: 'bg-primary',
    halo: 'bg-primary/10',
    accentText: 'text-primary',
    spinner: true,
  },
  redirecting: {
    eyebrow: 'Wir prüfen Deine Anmeldedaten ...',
    dot: 'bg-success',
    halo: 'bg-success/10',
    accentText: 'text-success',
    spinner: true,
  },
  success: {
    eyebrow: 'Erfolgreich',
    dot: 'bg-success',
    halo: 'bg-success/10',
    accentText: 'text-success',
    spinner: false,
  },
  missing: {
    eyebrow: 'Hinweis',
    dot: 'bg-warning',
    halo: 'bg-warning/10',
    accentText: 'text-warning',
    spinner: false,
  },
  error: {
    eyebrow: 'Problem',
    dot: 'bg-destructive',
    halo: 'bg-destructive/10',
    accentText: 'text-destructive',
    spinner: false,
  },
};

const PREVIEW_VIEWS: BridgeView[] = ['starting', 'login', 'authorizing', 'redirecting', 'success', 'missing', 'error'];
const MIN_STATUS_DISPLAY_MS = 1500;
const INITIAL_STATUS_DELAY_MS = 2000;
const LOGIN_REDIRECT_DELAY_MS = 1800;
const AUTHORIZING_STATUS_DELAY_MS = 650;
const REDIRECT_DELAY_MS = 1300;

const DEFAULT_TEST_PARAMS: Record<string, string> = {
  client_id: 'tri-mcp-playground',
  redirect_uri: 'https://example.com/oauth/callback',
  scope: 'openid profile mcp',
  state: 'demo-state-123',
  code_challenge: 'demo-code-challenge',
  code_challenge_method: 'S256',
};

const safeParseJson = (value: string | null) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const KNOWN_APP_HOSTS: Record<string, { label: string; icon: string }> = {
  'claude.ai': { label: 'Claude', icon: '/app-icons/claude.svg' },
  'www.claude.ai': { label: 'Claude', icon: '/app-icons/claude.svg' },
  'chatgpt.com': { label: 'ChatGPT', icon: '/app-icons/chatgpt.svg' },
  'www.chatgpt.com': { label: 'ChatGPT', icon: '/app-icons/chatgpt.svg' },
  'cursor.com': { label: 'Cursor', icon: '/app-icons/cursor.svg' },
  'www.cursor.com': { label: 'Cursor', icon: '/app-icons/cursor.svg' },
};

const getRedirectTargetInfo = (value: string) => {
  if (!value) {
    return { kind: 'unknown' as const, label: 'Deinem Ziel' };
  }

  try {
    const url = new URL(value);
    const protocol = url.protocol.toLowerCase();

    if (protocol === 'http:' || protocol === 'https:') {
      const knownApp = KNOWN_APP_HOSTS[url.host.toLowerCase()];
      if (knownApp) {
        return { kind: 'app' as const, label: knownApp.label, icon: knownApp.icon };
      }
      return { kind: 'website' as const, label: url.host || 'Deiner Zielseite' };
    }

    return { kind: 'app' as const, label: url.hostname || url.protocol.replace(':', '') || 'Deiner App' };
  } catch {
    if (value.includes('://')) {
      return { kind: 'app' as const, label: 'Deiner App' };
    }
    return { kind: 'unknown' as const, label: value };
  }
};

const getSuccessCopy = (redirectValue: string) => {
  const target = getRedirectTargetInfo(redirectValue);
  return {
    successTitle: 'Alles geklappt!',
    successMessage: `Du wirst automatisch zu ${target.label} weitergeleitet oder es erscheint ein Popup in Deinem Browser. Bitte bestätige dieses, um fortzufahren.`,
    successDetail: 'Anschließend kannst Du diese Seite sicher schließen.',
  };
};

const normalizePreview = (value: string | null): BridgeView | null => {
  if (!value) return null;
  return PREVIEW_VIEWS.includes(value as BridgeView) ? (value as BridgeView) : null;
};

const getPreviewStatus = (view: BridgeView, params: URLSearchParams): BridgeStatus => {
  const redirectUri = params.get('redirect_uri') || DEFAULT_TEST_PARAMS.redirect_uri;
  const successCopy = getSuccessCopy(redirectUri);
  const redirectTarget = getRedirectTargetInfo(redirectUri);
  const appInfo = redirectTarget.kind === 'app' && redirectTarget.icon
    ? { label: redirectTarget.label, icon: redirectTarget.icon }
    : undefined;
  const customTitle = params.get('preview_title');
  const customMessage = params.get('preview_message');

  const defaults: Record<BridgeView, BridgeStatus> = {
    starting: { view, title: '', message: '' },
    login: {
      view,
      title: 'Bitte kurz anmelden',
      message: 'Damit wir Dich sicher identifizieren koennen, leiten wir Dich jetzt zur Anmeldung weiter.',
      detail: 'Anschliessend geht es automatisch weiter.',
    },
    authorizing: {
      view,
      title: 'Deine Anmeldung wird bestaetigt',
      message: 'Wir pruefen Deine Freigabe und bereiten die Verbindung vor.',
      detail: 'Das dauert in der Regel nur einen Augenblick.',
    },
    redirecting: {
      view: 'success',
      title: successCopy.successTitle,
      message: successCopy.successMessage,
      detail: successCopy.successDetail,
      appInfo,
    },
    success: {
      view,
      title: successCopy.successTitle,
      message: successCopy.successMessage,
      detail: successCopy.successDetail,
      appInfo,
    },
    missing: {
      view,
      title: 'Die Seite wurde unvollständig aufgerufen',
      message: 'Einige Informationen fuer die Anmeldung fehlen.',
      detail: 'Bitte starte den Vorgang erneut oder wende Dich an Deinen Ansprechpartner.',
    },
    error: {
      view,
      title: 'Das hat leider nicht geklappt',
      message: 'Die Anmeldung konnte gerade nicht abgeschlossen werden.',
      detail: params.get('preview_error') || 'Bitte versuche es erneut oder wende Dich an info@the-real-insight.ai',
    },
  };

  return {
    ...defaults[view],
    title: customTitle || defaults[view].title,
    message: customMessage || defaults[view].message,
  };
};

/**
 * Der gezeichnete Haken bleibt erhalten: Strichlängen-Animationen lassen sich
 * nicht über Utility-Klassen ausdrücken, die Keyframes stehen deshalb weiter
 * inline. Die Farbe kommt jetzt über `currentColor` aus dem Statustoken.
 */
const AnimatedSuccessCheck = ({ accentText, halo }: { accentText: string; halo: string }) => (
  <div className={`relative flex h-[72px] w-[72px] items-center justify-center ${accentText}`}>
    <div className={`absolute inset-[6px] rounded-full ${halo} oauth-bridge-check-pulse`} />
    <svg viewBox="0 0 64 64" aria-hidden="true" className="block h-16 w-16 oauth-bridge-check-pop">
      <circle cx="32" cy="32" r="24" className="fill-card" stroke="currentColor" strokeWidth="2.5" />
      <path
        d="M22 33.5L29 40.5L43 25.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="oauth-bridge-check-draw"
      />
    </svg>
  </div>
);

export const OAuthBridgePage = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<BridgeStatus>({ view: 'starting', title: '', message: '' });
  const [logoFailed, setLogoFailed] = useState(false);

  const params = useMemo(() => new URLSearchParams(search), [search]);
  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const scope = params.get('scope') || '';
  const state = params.get('state') || '';
  const codeChallenge = params.get('code_challenge') || '';
  const codeChallengeMethod = params.get('code_challenge_method') || 'S256';
  const preview = normalizePreview(params.get('preview'));

  useEffect(() => {
    let cancelled = false;
    const timeoutIds: number[] = [];

    const updateStatus = (next: BridgeStatus) => {
      if (!cancelled) setStatus(next);
    };

    const scheduleTimeout = (cb: () => void, ms: number) => {
      const id = window.setTimeout(cb, ms);
      timeoutIds.push(id);
      return id;
    };

    const wait = (ms: number) => new Promise<void>((resolve) => { scheduleTimeout(resolve, ms); });

    if (preview) {
      updateStatus(getPreviewStatus(preview, params));
      return () => { cancelled = true; };
    }

    if (!clientId || !redirectUri || !codeChallenge) {
      updateStatus({
        view: 'missing',
        title: 'Die Seite wurde unvollstaendig aufgerufen',
        message: 'Einige Informationen fuer die Anmeldung fehlen.',
        detail: 'Bitte starte den Vorgang erneut oder wende Dich an Deinen Ansprechpartner.',
      });
      return () => { cancelled = true; };
    }

    const storedTokens = safeParseJson(localStorage.getItem('tri.jwt.tokens'));
    const accessToken = storedTokens?.accessToken;

    if (!accessToken) {
      updateStatus({
        view: 'login',
        title: 'Bitte kurz anmelden',
        message: 'Damit wir Dich sicher identifizieren koennen, leiten wir Dich jetzt zur Anmeldung weiter.',
        detail: 'Anschliessend geht es automatisch weiter.',
      });

      const redirectTarget = `/oauth/bridge${search}`;
      const loginUrl = `/login?redirect=${encodeURIComponent(redirectTarget)}`;
      scheduleTimeout(() => {
        if (!cancelled) navigate(loginUrl);
      }, LOGIN_REDIRECT_DELAY_MS);

      return () => { cancelled = true; timeoutIds.forEach((id) => window.clearTimeout(id)); };
    }

    const submit = async () => {
      try {
        await wait(INITIAL_STATUS_DELAY_MS);
        if (cancelled) return;

        updateStatus({
          view: 'authorizing',
          title: 'Deine Anmeldung wird bestätigt',
          message: 'Wir prüfen Deine Freigabe und bereiten die Verbindung vor.',
          detail: 'Das dauert in der Regel nur einen Augenblick.',
        });

        await wait(AUTHORIZING_STATUS_DELAY_MS);
        if (cancelled) return;

        const [response] = await Promise.all([
          fetch(`${configuration.serverUrl}/oauth/authorize/bridge`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              client_id: clientId,
              redirect_uri: redirectUri,
              scope,
              state,
              code_challenge: codeChallenge,
              code_challenge_method: codeChallengeMethod,
            }),
          }),
          wait(MIN_STATUS_DISPLAY_MS),
        ]);

        if (cancelled) return;

        const data = await response.json().catch(() => null);

        if (!response.ok) {
          updateStatus({
            view: 'error',
            title: 'Das hat leider nicht geklappt',
            message: 'Die Anmeldung konnte gerade nicht abgeschlossen werden.',
            detail: typeof data === 'string' ? data : JSON.stringify(data),
          });
          return;
        }

        if (!data?.redirectUrl) {
          updateStatus({
            view: 'error',
            title: 'Die Weiterleitung ist gerade nicht verfuegbar',
            message: 'Die Anmeldung konnte nicht vollstaendig vorbereitet werden.',
            detail: 'Bitte versuche es erneut oder wende Dich an Deinen Ansprechpartner.',
          });
          return;
        }

        const redirectCopy = getSuccessCopy(data.redirectUrl);
        const redirectTarget = getRedirectTargetInfo(data.redirectUrl);
        const appInfo = redirectTarget.icon
          ? { label: redirectTarget.label, icon: redirectTarget.icon }
          : undefined;

        updateStatus({
          view: 'success',
          title: redirectCopy.successTitle,
          message: redirectCopy.successMessage,
          detail: redirectCopy.successDetail,
          appInfo,
        });

        scheduleTimeout(() => {
          if (!cancelled) window.location.assign(data.redirectUrl);
        }, REDIRECT_DELAY_MS);

      } catch (error: any) {
        updateStatus({
          view: 'error',
          title: 'Das hat leider nicht geklappt',
          message: 'Die Anmeldung konnte gerade nicht abgeschlossen werden.',
          detail: error?.message || 'Bitte versuche es erneut oder wende Dich an Deinen Ansprechpartner.',
        });
      }
    };

    submit();

    return () => { cancelled = true; timeoutIds.forEach((id) => window.clearTimeout(id)); };
  }, [clientId, codeChallenge, codeChallengeMethod, navigate, params, preview, redirectUri, scope, search, state]);

  const viewStyle = VIEW_STYLES[status.view];

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background p-4">
      <style>
        {`
          .oauth-bridge-check-pop { animation: oauthBridgeCheckPop 420ms ease-out both; }
          .oauth-bridge-check-draw {
            stroke-dasharray: 32;
            stroke-dashoffset: 32;
            animation: oauthBridgeCheckDraw 500ms 180ms ease forwards;
          }
          .oauth-bridge-check-pulse { animation: oauthBridgeCheckPulse 700ms ease-out both; }
          @keyframes oauthBridgeCheckPop {
            0% { transform: scale(0.82); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes oauthBridgeCheckDraw {
            from { stroke-dashoffset: 32; }
            to { stroke-dashoffset: 0; }
          }
          @keyframes oauthBridgeCheckPulse {
            0% { transform: scale(0.7); opacity: 0; }
            70% { transform: scale(1.08); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
          @media (prefers-reduced-motion: reduce) {
            .oauth-bridge-check-pop,
            .oauth-bridge-check-draw,
            .oauth-bridge-check-pulse { animation: none; }
            .oauth-bridge-check-draw { stroke-dashoffset: 0; }
          }
        `}
      </style>

      {!logoFailed ? (
        <img
          src={BRAND.squareLogo}
          alt={BRAND.name}
          width={72}
          height={72}
          onError={() => setLogoFailed(true)}
          className="h-[72px] w-[72px] object-contain"
        />
      ) : (
        <div className="flex h-[72px] w-[72px] items-center justify-center rounded-2xl bg-foreground text-xl font-bold text-background">
          {BRAND.fallbackInitials}
        </div>
      )}

      <p className="text-sm text-muted-foreground">{viewStyle.eyebrow}</p>

      <Card
        key={`${status.view}-${status.title}`}
        className="w-full max-w-md animate-in fade-in slide-in-from-bottom-2 duration-300"
      >
        <CardContent className="flex flex-col items-center gap-4 p-6 text-center">
          <h1 className="text-xl font-semibold">{status.title}</h1>

          <p className="text-sm leading-relaxed">{status.message}</p>

          {status.detail && (
            <p className="break-words text-sm leading-relaxed text-muted-foreground">{status.detail}</p>
          )}

          <div className="flex justify-center pt-2">
            {viewStyle.spinner ? (
              <Loader2
                aria-hidden="true"
                className={`h-8 w-8 animate-spin ${viewStyle.accentText}`}
              />
            ) : status.view === 'success' ? (
              <AnimatedSuccessCheck accentText={viewStyle.accentText} halo={viewStyle.halo} />
            ) : (
              <div className={`flex h-7 w-7 items-center justify-center rounded-full ${viewStyle.halo}`}>
                <div className={`h-3 w-3 rounded-full ${viewStyle.dot}`} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
